/**
 * iter200 — Reusable address input with Google Places (New) autocomplete
 * and Google Address Validation ("Verify" button).
 *
 * Design contract
 * ---------------
 * Controlled input. The parent owns ``value`` (a plain string) and gets
 * called back with structured components on selection or successful
 * verification via ``onChange``. All Google calls go through the
 * backend proxy (``/api/google-addresses/*``) — the API key never
 * touches the browser.
 *
 * Props
 * -----
 *   value            string  — current textual address (controlled)
 *   onChange(next)   fn      — called with either:
 *                                 { text }                          (typing)
 *                                 { text, components, formatted,
 *                                   source: "autocomplete"|"verify",
 *                                   verified: boolean }
 *   regionCode       "US"|"CA"|... optional bias for autocomplete
 *   verifyLocality       optional field-hint passed to verify-address
 *   verifyAdminArea      optional
 *   verifyPostalCode     optional
 *   placeholder      string
 *   testid           string  — data-testid prefix
 *   disabled         boolean
 *
 * Session tokens
 * --------------
 * A fresh UUID is generated per typing burst and reused across the
 * autocomplete + place-details pair — that's what Google bills as a
 * single "session". A new token starts after each selection.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, CheckCircle, Warning, Spinner, Check } from "@phosphor-icons/react";
import api from "@/lib/api";

const AUTO_DEBOUNCE_MS = 300;
const AUTO_MIN_CHARS = 3;


function newSessionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Poly-ish fallback for the rare browser that lacks it.
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}


export default function AddressAutocomplete({
  value = "",
  onChange,
  regionCode,
  verifyLocality,
  verifyAdminArea,
  verifyPostalCode,
  placeholder = "Start typing an address…",
  testid = "address-autocomplete",
  disabled = false,
  className = "",
}) {
  const [items, setItems] = useState([]);        // autocomplete predictions
  const [open, setOpen] = useState(false);        // dropdown open?
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [error, setError] = useState(null);
  const sessionRef = useRef(newSessionToken());
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const lastInputRef = useRef(value);
  const skipNextFetchRef = useRef(false);   // suppress fetch immediately after selection

  // ── Debounced autocomplete on ``value`` change ──
  useEffect(() => {
    lastInputRef.current = value;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    if (!value || value.trim().length < AUTO_MIN_CHARS) {
      setItems([]);
      setOpen(false);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoadingSuggest(true);
      try {
        const r = await api.post("/google-addresses/autocomplete", {
          input:        value.trim(),
          sessionToken: sessionRef.current,
          regionCode:   regionCode || undefined,
        });
        // Ignore stale responses if the user has typed again.
        if (lastInputRef.current !== value) return;
        setItems(r.data?.suggestions || []);
        setOpen((r.data?.suggestions || []).length > 0);
      } catch (e) {
        // 503 = key not configured; anything else = upstream problem.
        setItems([]);
        setOpen(false);
        setError(e?.response?.data?.detail || null);
      } finally {
        setLoadingSuggest(false);
      }
    }, AUTO_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [value, regionCode]);

  // ── Click-outside collapses the dropdown ──
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleTyping = (e) => {
    const next = e.target.value;
    setError(null);
    setVerifyResult(null);
    onChange?.({ text: next });
  };

  const pickSuggestion = useCallback(async (sug) => {
    setOpen(false);
    setError(null);
    setLoadingSuggest(true);
    try {
      const r = await api.get(
        `/google-addresses/place-details/${encodeURIComponent(sug.placeId)}`,
        { params: { sessionToken: sessionRef.current } },
      );
      const formatted = r.data?.formattedAddress || sug.text;
      // New session for the next typing burst per Google guidance.
      sessionRef.current = newSessionToken();
      skipNextFetchRef.current = true;
      onChange?.({
        text:       formatted,
        formatted,
        components: r.data?.components || {},
        placeId:    r.data?.placeId,
        source:     "autocomplete",
        verified:   true,
      });
      setVerifyResult({ label: "verified", formatted, viaAutocomplete: true });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load address details");
    } finally {
      setLoadingSuggest(false);
    }
  }, [onChange]);

  const runVerify = useCallback(async () => {
    if (!value || !value.trim()) return;
    setError(null);
    setVerifyBusy(true);
    setOpen(false);
    try {
      const r = await api.post("/google-addresses/verify-address", {
        addressLines:       [value.trim()],
        regionCode:         regionCode || undefined,
        locality:           verifyLocality || undefined,
        administrativeArea: verifyAdminArea || undefined,
        postalCode:         verifyPostalCode || undefined,
        sessionToken:       sessionRef.current,
      });
      setVerifyResult({
        label:      r.data?.verdictLabel || "review",
        formatted:  r.data?.formattedAddress || "",
        components: r.data?.components || {},
        missing:    r.data?.missing || [],
        unconfirmed:r.data?.unconfirmed || [],
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not verify address");
    } finally {
      setVerifyBusy(false);
    }
  }, [value, regionCode, verifyLocality, verifyAdminArea, verifyPostalCode]);

  const applySuggested = useCallback(() => {
    if (!verifyResult?.formatted) return;
    sessionRef.current = newSessionToken();
    skipNextFetchRef.current = true;
    onChange?.({
      text:       verifyResult.formatted,
      formatted:  verifyResult.formatted,
      components: verifyResult.components || {},
      source:     "verify",
      verified:   true,
    });
    setVerifyResult({ ...verifyResult, label: "verified" });
  }, [verifyResult, onChange]);

  const banner = useMemo(() => {
    if (!verifyResult) return null;
    const { label, formatted, missing, unconfirmed, viaAutocomplete } = verifyResult;
    // Normalise for the "differs?" comparison — trim + collapse
    // whitespace + case-insensitive, so a mismatch only fires when
    // there's a real content difference (postal-code casing, missing
    // commas, junk characters, etc.).
    const norm = (s) => (s || "").trim().replace(/\s+/g, " ").toLowerCase();
    const suggestedDiffers = !!formatted && norm(formatted) !== norm(value);

    // Verified path: two flavours.
    //   • Autocomplete selection → simple green line.
    //   • Verify button → if Google cleaned the input (e.g. stripped
    //     "???" from a postal code), still surface the clean version
    //     even though the verdict label is "verified", because the raw
    //     input is what we'd save to the DB.
    if (label === "verified") {
      if (!viaAutocomplete && suggestedDiffers) {
        return (
          <div data-testid={`${testid}-verified-cleanup`}
               className="mt-1 text-[11px] px-2 py-1.5 border rounded-sm border-emerald-300 bg-emerald-50 text-emerald-900 space-y-1">
            <div className="flex items-center gap-1.5 font-semibold">
              <CheckCircle size={12} weight="fill" />
              Address verified — cleaned version available
            </div>
            <div className="text-slate-700">
              Google cleaned the address. Save the tidy version?
            </div>
            <div className="text-slate-700">
              Suggested: <span className="font-medium">{formatted}</span>
            </div>
            <button type="button"
                    data-testid={`${testid}-apply-suggested`}
                    onClick={applySuggested}
                    className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800">
              <Check size={11} weight="bold" /> Use cleaned address
            </button>
          </div>
        );
      }
      return (
        <div data-testid={`${testid}-verified`}
             className="mt-1 flex items-center gap-1.5 text-[11px] text-emerald-800">
          <CheckCircle size={12} weight="fill" />
          {viaAutocomplete ? "Address selected from Google" : "Address verified"}
        </div>
      );
    }
    return (
      <div data-testid={`${testid}-warning`}
           className="mt-1 text-[11px] px-2 py-1.5 border rounded-sm border-amber-300 bg-amber-50 text-amber-900 space-y-1">
        <div className="flex items-center gap-1.5 font-semibold">
          <Warning size={12} weight="fill" />
          {label === "fix" ? "Address needs a fix" :
           label === "confirm" ? "Please confirm address" : "Address needs review"}
        </div>
        {(missing?.length || 0) > 0 && (
          <div>Missing: <span className="font-mono">{missing.join(", ")}</span></div>
        )}
        {(unconfirmed?.length || 0) > 0 && (
          <div>Unconfirmed: <span className="font-mono">{unconfirmed.join(", ")}</span></div>
        )}
        {suggestedDiffers && (
          <div className="pt-1">
            <div className="text-slate-700">Suggested: <span className="font-medium">{formatted}</span></div>
            <button type="button"
                    data-testid={`${testid}-apply-suggested`}
                    onClick={applySuggested}
                    className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-sm bg-amber-700 text-white hover:bg-amber-800">
              <Check size={11} weight="bold" /> Use suggested address
            </button>
          </div>
        )}
      </div>
    );
  }, [verifyResult, value, applySuggested, testid]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex gap-1">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={handleTyping}
            onFocus={() => items.length && setOpen(true)}
            placeholder={placeholder}
            autoComplete="street-address"
            disabled={disabled}
            data-testid={`${testid}-input`}
            className="w-full px-3 py-2 pr-8 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
          />
          {loadingSuggest && (
            <Spinner size={14} className="absolute right-2 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
          )}
          {!loadingSuggest && (
            <MagnifyingGlass size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300" />
          )}
        </div>
        <button
          type="button"
          onClick={runVerify}
          disabled={disabled || verifyBusy || !value?.trim()}
          data-testid={`${testid}-verify-btn`}
          title="Check this address with Google"
          className="text-[11px] uppercase font-bold tracking-wider px-2.5 rounded-sm border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 inline-flex items-center gap-1">
          {verifyBusy ? <Spinner size={11} className="animate-spin" /> : <CheckCircle size={11} weight="bold" />}
          Verify
        </button>
      </div>

      {open && items.length > 0 && (
        <ul data-testid={`${testid}-suggestions`}
            className="absolute z-30 left-0 right-0 mt-1 max-h-64 overflow-auto bg-white border border-slate-200 rounded-sm shadow-lg">
          {items.map((sug) => (
            <li key={sug.placeId}>
              <button
                type="button"
                data-testid={`${testid}-suggestion-${sug.placeId}`}
                onClick={() => pickSuggestion(sug)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-b-0">
                <div className="font-medium text-slate-900 truncate">{sug.mainText || sug.text}</div>
                {sug.secondaryText && (
                  <div className="text-[11px] text-slate-500 truncate">{sug.secondaryText}</div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <div data-testid={`${testid}-error`}
             className="mt-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-sm px-2 py-1">
          {error}
        </div>
      )}
      {banner}
    </div>
  );
}
